import { useSessionTokenGetter } from '@develio/auth/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

type RimsDataset = {
  id: string;
  regionName: string;
  regionType: string;
  multiplierType: string;
  ioVintageYear: number | null;
  regionalVintageYear: number | null;
  sourceFilename: string;
  status: 'uploaded' | 'parsing' | 'ready' | 'failed';
  errorMessage: string | null;
  rowCount: number | null;
  createdAt: string;
};

const STATUS_BADGE: Record<string, string> = {
  uploaded: 'bg-slate-100 text-slate-600',
  parsing: 'bg-amber-100 text-amber-700',
  ready: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
};

export function AdminRimsRoute() {
  const getToken = useSessionTokenGetter();
  const [datasets, setDatasets] = useState<RimsDataset[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const fetchDatasets = useCallback(async () => {
    const token = await getToken();
    const res = await fetch(`${API}/api/v1/admin/rims-national`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const j = (await res.json()) as { datasets: RimsDataset[] };
      setDatasets(j.datasets);
    }
    setLoading(false);
  }, [getToken]);

  useEffect(() => {
    void fetchDatasets();
  }, [fetchDatasets]);

  // Poll while any dataset is still processing
  useEffect(() => {
    const pending = datasets.some((d) => d.status === 'uploaded' || d.status === 'parsing');
    if (!pending) return;
    const timer = setInterval(() => void fetchDatasets(), 3000);
    return () => clearInterval(timer);
  }, [datasets, fetchDatasets]);

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const token = await getToken();
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API}/api/v1/admin/rims-national`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const j = await res.json();
      if (!res.ok) {
        setUploadError((j as { error?: string }).error ?? `Error ${res.status}`);
      } else {
        if (fileRef.current) fileRef.current.value = '';
        await fetchDatasets();
      }
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      const token = await getToken();
      await fetch(`${API}/api/v1/admin/rims-national/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchDatasets();
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-6 py-10 space-y-8">
      <div>
        <Link to="/admin" className="text-xs text-slate-400 hover:underline">
          ← All organizations
        </Link>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight">National RIMS II Data</h1>
        <p className="mt-1 text-sm text-slate-500">
          Platform-wide multipliers served to trial users who haven't uploaded their own RIMS II
          data. Upload a BEA Table 1.5 Total Multipliers .xlsx file.
        </p>
      </div>

      {/* Info banner */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
        <p className="font-semibold mb-1">How this works</p>
        <p>
          When a trial user opens the analysis wizard, national datasets appear alongside any
          org-specific uploads, labelled <em>"National avg — trial"</em>. Paid users can upload
          their own regional RIMS II data for precise local multipliers. Raw multiplier values are
          never exposed in API responses — only calculated impact outputs.
        </p>
      </div>

      {/* Upload card */}
      <div className="rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold text-slate-700">Upload dataset</h2>
        <div className="flex items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="block text-sm text-slate-600 file:mr-3 file:rounded-md file:border file:border-slate-300 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-50"
          />
          <button
            type="button"
            onClick={() => void handleUpload()}
            disabled={uploading}
            className="shrink-0 rounded-md bg-slate-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
          >
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
        {uploadError && <p className="mt-2 text-xs text-red-600">{uploadError}</p>}
        <p className="mt-2 text-xs text-slate-400">
          BEA "Table 1.5 Total Multipliers" format · max 10 MB · .xlsx only
        </p>
      </div>

      {/* Dataset list */}
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">
            National datasets ({datasets.length})
          </h2>
          {datasets.some((d) => d.status === 'parsing' || d.status === 'uploaded') && (
            <span className="text-xs text-amber-600 animate-pulse">Parsing…</span>
          )}
        </div>

        {loading && <div className="px-5 py-8 text-center text-sm text-slate-400">Loading…</div>}

        {!loading && datasets.length === 0 && (
          <div className="px-5 py-8 text-center text-sm text-slate-400">
            No national datasets uploaded yet. Upload a BEA Table 1.5 .xlsx to get started.
          </div>
        )}

        {datasets.length > 0 && (
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500">Region</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500">Type</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500">
                  Vintage
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500">Rows</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500">Status</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {datasets.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-900">{d.regionName}</p>
                    <p className="text-xs text-slate-400">{d.sourceFilename}</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600 capitalize">
                    {d.multiplierType?.replace('_', ' ') ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {d.regionalVintageYear
                      ? `${d.ioVintageYear ?? '?'} I/O · ${d.regionalVintageYear} regional`
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{d.rowCount ?? '—'}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_BADGE[d.status] ?? 'bg-slate-100 text-slate-600'}`}
                    >
                      {d.status}
                    </span>
                    {d.errorMessage && (
                      <p className="mt-0.5 text-xs text-red-600 max-w-xs truncate">
                        {d.errorMessage}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => void handleDelete(d.id)}
                      disabled={deletingId === d.id}
                      className="text-xs text-red-500 hover:text-red-700 disabled:opacity-40"
                    >
                      {deletingId === d.id ? 'Deleting…' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
